import type { InvoicePayment } from '@/components/invoices/InvoiceDocument';

// Derives payment (COD collection) status from core_orders.status — never
// stored separately, core_orders/core_shipments stay the single source.
export type PaymentStatusTone = 'success' | 'warning' | 'neutral';

export interface PaymentStatusInfo {
  label: string;
  tone: PaymentStatusTone;
}

const PAID_STATUSES = new Set(['cod_collected', 'remitted', 'settled']);
const CANCELLED_STATUSES = new Set(['cancelled', 'returned']);
// In-store methods are collected at the point of sale, in person — unlike
// cod (paid later, on delivery) or bkash/nagad/bank (paid online, but not
// necessarily confirmed yet), so these count as paid immediately regardless
// of order status. 'prepaid' is deliberately NOT in this set — it means
// "not collecting COD" at booking time, not "already confirmed paid";
// it shows as Due until staff edits the order to the actual method once
// known and/or records the payment.
const INSTORE_PAID_METHODS = new Set(['cash', 'pos_card']);

// Shared label for invoice/receipt "Payment Method" rows — keeps 'cash' and
// 'pos_card' from printing as raw 'CASH'/'POS_CARD'. For an online gateway
// key (sslcommerz/bkash/nagad/bank) not in the hardcoded list above,
// `gatewayDisplayNames` (a gateway_key -> display_name map, sourced from
// core_payment_gateways / core_payment_gateways_public_v — see callers of
// buildInvoicePaymentInfo below) resolves the real configured name instead
// of just uppercasing the raw key.
export function formatPaymentMethodLabel(method: string | null | undefined, gatewayDisplayNames?: Record<string, string>): string {
  if (!method || method === 'cod') return 'Cash on Delivery';
  if (method === 'cash') return 'Cash (In-store)';
  if (method === 'pos_card') return 'Card (POS)';
  if (method === 'gift') return 'Gift (No Charge)';
  if (method === 'prepaid') return 'Prepaid / No COD';
  return gatewayDisplayNames?.[method] ?? method.toUpperCase();
}

export function getPaymentStatus(orderStatus: string, paymentMethod?: string | null): PaymentStatusInfo {
  if (CANCELLED_STATUSES.has(orderStatus)) return { label: 'Cancelled', tone: 'neutral' };
  if (paymentMethod === 'gift') return { label: 'Gift', tone: 'neutral' };
  if (PAID_STATUSES.has(orderStatus) || (paymentMethod && INSTORE_PAID_METHODS.has(paymentMethod))) {
    return { label: 'Paid', tone: 'success' };
  }
  if (paymentMethod === 'prepaid') return { label: 'Due', tone: 'warning' };
  const methodLabel = !paymentMethod || paymentMethod === 'cod' ? 'COD' : paymentMethod.toUpperCase();
  return { label: methodLabel, tone: 'warning' };
}

// Printed "Payment Status" on invoices/receipts — a gift order was never
// charged, so printing "Unpaid" reads as a debt owed that doesn't exist.
// Matches the 'Gift' label/neutral tone getPaymentStatus() already uses
// elsewhere, instead of forcing gift through the paid/unpaid boolean.
export function paymentStatusLabel(payment: InvoicePayment): string {
  if (payment.method === 'gift') return 'Gift';
  return payment.paid ? 'Paid' : 'Unpaid';
}

export function paymentStatusColor(payment: InvoicePayment): string {
  if (payment.method === 'gift') return '#6B7280';
  return payment.paid ? '#2F7A4A' : '#B7461F';
}

// Richer version for the invoice document itself — the amount actually
// collected (cod_amount_bdt) and when (delivered_at is when a COD courier
// hands over cash, there's no separate "collected_at" column) rather than
// just a status word.
export function buildInvoicePaymentInfo(
  orderStatus: string,
  paymentMethod: string | null,
  codAmountBdt: number | null,
  deliveredAt: string | null,
  gatewayDisplayNames?: Record<string, string>,
): InvoicePayment {
  const paid = PAID_STATUSES.has(orderStatus) || (!!paymentMethod && INSTORE_PAID_METHODS.has(paymentMethod));
  return {
    paid,
    method: paymentMethod,
    methodLabel: formatPaymentMethodLabel(paymentMethod, gatewayDisplayNames),
    amountBdt: paid ? codAmountBdt : null,
    paidAt: paid ? deliveredAt : null,
  };
}
